<script>
export default {
	name: 'SubjectProfileCard',
	props: {
		user: {
			type: Object,
			required: true,
		},
		regions: {
			type: String,
			default: '',
		},
		pools: {
			type: String,
			default: null,
		},
	},
	computed: {
		gbd() {
			return (this.user.user_info && this.user.user_info.obj) || null
		},
		fio() {
			return [this.user.last_name, this.user.first_name, this.user.middle_name]
				.filter(Boolean)
				.join(' ')
		},
		title() {
			return this.user.name_company || this.fio || this.user.iin_bin
		},
		subtitle() {
			return this.user.name_company && this.fio ? this.fio : ''
		},
		initials() {
			const words = String(this.title || '')
				.replace(/[«»"']/g, ' ')
				.split(/\s+/)
				.filter((word) => word && !/^(ТОО|ИП|АО|КХ|ПК|ОО|ГУ|РГП|ГКП|LLP|ЖШС)$/i.test(word))
			return words
				.slice(0, 2)
				.map((word) => word[0])
				.join('')
				.toUpperCase()
		},
		phone() {
			const phone = this.user.phone
			if (!phone) return ''
			return String(phone)[0] === '7' ? `+${phone}` : phone
		},
		address() {
			if (!this.gbd || !this.gbd.Address) return ''
			const suffix = this.$i18n.locale === 'kz' ? 'Kz' : 'Ru'
			return ['District', 'Region', 'Street']
				.map((part) => this.gbd.Address[part + suffix])
				.filter(Boolean)
				.join(', ')
		},
		groups() {
			const groups = [
				{
					key: 'registration',
					title: this.$t('profile_ui.registration'),
					rows: [
						{
							label: this.$t('headers.oced'),
							value: this.gbd && this.gbd.Activity ? this.gbd.Activity.OKED : '',
						},
						{
							label: this.$t('headers.kato'),
							value: this.gbd && this.gbd.Address ? this.gbd.Address.KATO : '',
						},
						{ label: this.$t('headers.address_registration'), value: this.address },
					],
				},
				{
					key: 'contacts',
					title: this.$t('profile_ui.contacts'),
					rows: [
						{ label: 'Email', value: this.user.email },
						{ label: this.$t('site.registration.phone'), value: this.phone },
					],
				},
				{
					key: 'quotas',
					title: this.$t('profile_ui.quotas'),
					rows: [
						{
							label: this.$t('headers.quota_region'),
							value: this.regions || this.$t('labels.no_quotas'),
						},
						this.pools !== null && {
							label: this.$t('headers.pool_quotas'),
							value: this.pools || this.$t('labels.no_quotas'),
						},
					].filter(Boolean),
				},
			]
			return groups
				.map((group) => ({ ...group, rows: group.rows.filter((row) => row.value) }))
				.filter((group) => group.rows.length)
		},
		registeredAt() {
			const format = this.$options.filters.formatOnlyDate
			return this.user.created_at && format ? format(this.user.created_at) : ''
		},
	},
	methods: {
		localized(value) {
			if (!value) return ''
			return this.$i18n.locale === 'kz' ? value.NameKz || value.NameRu : value.NameRu
		},
		async copyIinBin() {
			try {
				await navigator.clipboard.writeText(this.user.iin_bin)
				this.$toast.success(this.$t('profile_ui.copied'))
			} catch (error) {
				this.$toast.error(this.$t('system_message.error_occurred'))
			}
		},
	},
}
</script>

<template>
	<section class="sp-card">
		<button type="button" class="sp-card__back" @click="$router.go(-1)">
			<svg
				width="16"
				height="16"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2.2"
				aria-hidden="true"
			>
				<path d="M15 5l-7 7 7 7" />
			</svg>
			{{ $t('aqua.ui.back') }}
		</button>

		<div class="aq-card sp-card__hero">
			<div class="sp-card__identity">
				<div class="sp-card__avatar" aria-hidden="true">{{ initials }}</div>
				<div class="sp-card__text">
					<div class="sp-card__eyebrow">{{ $t('labels.subject_profile') }}</div>
					<h1 class="sp-card__title">{{ title }}</h1>
					<p v-if="subtitle" class="sp-card__subtitle">{{ subtitle }}</p>
					<div class="sp-card__meta">
						<button
							v-if="user.iin_bin"
							type="button"
							class="aq-badge aq-badge--neutral sp-card__copy"
							:title="$t('profile_ui.copy_iin')"
							@click="copyIinBin"
						>
							{{ $t('headers.iin_bin') }} {{ user.iin_bin }}
							<svg
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								aria-hidden="true"
							>
								<rect x="9" y="9" width="11" height="11" rx="2" />
								<path d="M5 15V5a2 2 0 0 1 2-2h10" />
							</svg>
						</button>
						<span v-if="gbd && gbd.RegStatus" class="aq-badge">{{ localized(gbd.RegStatus) }}</span>
						<span v-if="gbd && gbd.OrgForm" class="aq-badge aq-badge--neutral">{{
							localized(gbd.OrgForm)
						}}</span>
						<span v-if="registeredAt" class="sp-card__date">{{
							$t('profile_ui.registered', { date: registeredAt })
						}}</span>
					</div>
				</div>
			</div>

			<div v-if="groups.length" class="sp-card__groups">
				<div v-for="group in groups" :key="group.key" class="sp-card__group">
					<div class="sp-card__group-title">{{ group.title }}</div>
					<dl class="sp-card__list">
						<div v-for="row in group.rows" :key="row.label" class="sp-card__row">
							<dt>{{ row.label }}</dt>
							<dd>{{ row.value }}</dd>
						</div>
					</dl>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped>
.sp-card__back {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 12px;
	padding: 0;
	border: 0;
	background: none;
	color: var(--aq-muted);
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: color 0.2s var(--aq-ease);
}
.sp-card__back:hover,
.sp-card__back:focus-visible {
	color: var(--aq-primary);
	outline: none;
}
.sp-card__hero {
	padding: 24px;
}
.sp-card__identity {
	display: flex;
	align-items: flex-start;
	gap: 18px;
}
.sp-card__avatar {
	display: flex;
	flex: 0 0 64px;
	align-items: center;
	justify-content: center;
	width: 64px;
	height: 64px;
	border-radius: 16px;
	background: linear-gradient(135deg, var(--aq-primary) 0%, #4fa3ff 100%);
	color: #fff;
	font-size: 22px;
	font-weight: 700;
	letter-spacing: 0.02em;
}
.sp-card__text {
	min-width: 0;
	flex: 1;
}
.sp-card__eyebrow {
	margin-bottom: 4px;
	color: var(--aq-primary);
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
}
.sp-card__title {
	margin: 0;
	color: var(--aq-ink);
	font-size: 26px;
	font-weight: 700;
	line-height: 1.25;
	overflow-wrap: anywhere;
}
.sp-card__subtitle {
	margin: 4px 0 0;
	color: var(--aq-ink-2);
	font-size: 15px;
}
.sp-card__meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 10px;
	margin-top: 12px;
}
.sp-card__copy {
	border: 0;
	cursor: pointer;
	font-variant-numeric: tabular-nums;
	transition: background 0.2s var(--aq-ease), color 0.2s var(--aq-ease);
}
.sp-card__copy:hover,
.sp-card__copy:focus-visible {
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
	outline: none;
}
.sp-card__date {
	color: var(--aq-muted);
	font-size: 13px;
}
.sp-card__groups {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
	gap: 16px;
	margin-top: 24px;
	padding-top: 20px;
	border-top: 1px solid var(--aq-border);
}
.sp-card__group {
	padding: 16px 18px;
	border-radius: var(--aq-radius);
	background: var(--aq-bg);
}
.sp-card__group-title {
	margin-bottom: 12px;
	color: var(--aq-muted);
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
}
.sp-card__list {
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin: 0;
}
.sp-card__row dt {
	margin-bottom: 2px;
	color: var(--aq-muted);
	font-size: 13px;
	font-weight: 500;
}
.sp-card__row dd {
	margin: 0;
	color: var(--aq-ink);
	font-size: 15px;
	line-height: 1.45;
	overflow-wrap: anywhere;
}
@media (max-width: 768px) {
	.sp-card__hero {
		padding: 18px;
	}
	.sp-card__avatar {
		flex-basis: 48px;
		width: 48px;
		height: 48px;
		border-radius: 12px;
		font-size: 17px;
	}
	.sp-card__title {
		font-size: 21px;
	}
}
</style>
